import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';

import useProductSchema from '../../../../hooks/schema/useProductSchema';
import { modelOf } from '../../../../prop-types';
import { LocationContextPropType } from '../../../../services/LocationContext';
import RouteService from '../../../../services/RouteService';
import AccountStore from '../../../../store/AccountStore';
import CategoryStore from "../../../../store/CategoryStore";
import ConfigStore from '../../../../store/ConfigStore';
import CountryStore from "../../../../store/CountryStore";
import CurrencyStore from '../../../../store/CurrencyStore';
import SEOSchema from '../../../head/SEOSchema';
import Product from "../../../../models/Product";
import SchemaType from "../../../../types/SchemaType";
import UIStore from "../../../../store/UIStore";
import CommonPage from "../../../../types/CommonPage";

const ProductListSchema = ({
  accountStore,
  categoryStore,
  configStore,
  countryStore,
  currencyStore,
  uiStore,
  routeService,
  locationContext,
  products,
}) => {
  const [getProductSchema] = useProductSchema({
    accountStore,
    categoryStore,
    configStore,
    countryStore,
    currencyStore,
    routeService,
    locationContext,
  });

  const title = categoryStore?.activeCategory?.SEO_title || categoryStore?.activeCategory?.name || '';
  let name = configStore.store.name + (title.length > 0
    ? ` - ${title}`
    : '');
  const description = categoryStore?.activeCategory?.SEO_description;

  const itemListElement = products?.map((product) => {
    /* Use only main product as it is presented in the listings */
    return getProductSchema(product);
  });

  // Frontpage has the WebPage type
  const data = uiStore.currentPage === CommonPage.HOME
    ? {
        '@context': 'https://schema.org',
        '@type': SchemaType.ITEM_LIST,
        itemListElement: itemListElement,
        numberOfItems: products?.length || 0,
      }
    : {
        '@context': 'https://schema.org',
        '@type': SchemaType.COLLECTION_PAGE,
        name: name,
        url: window.location.href,
        ...(description && { description }),
        mainEntity: {
          '@type': SchemaType.ITEM_LIST,
          itemListElement: itemListElement,
          numberOfItems: products?.length || 0,
        },
      };

  return <SEOSchema data={data}/>;
};

ProductListSchema.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  locationContext: LocationContextPropType.isRequired,
  products: PropTypes.arrayOf(modelOf(Product)),
};

export default inject(
  'accountStore',
  'categoryStore',
  'configStore',
  'countryStore',
  'currencyStore',
  'uiStore',
  'routeService',
  'locationContext'
)(observer(ProductListSchema));
